import { Suspense, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { maskWholeNumber } from '@givebutter/util';
import {
    faArrowDownToBracket,
    faArrowsRotate,
    faTrash,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchImports } from 'api/imports';
import { Badge } from 'assets/js/pages/account-settings/imports/Badge';
import * as DeleteImportAlertDialog from 'assets/js/pages/account-settings/imports/DeleteImportAlertDialog';
import { errorCount, isDeletableImport } from 'assets/js/pages/account-settings/imports/helpers';
import * as DropdownMenu from 'components/dropdown-menu';
import { importFormatted } from 'components/imports/importStrategy';
import { ReactQueryErrorBoundary } from 'components/react-query-error-boundary';
import { useReactTableState } from 'components/table';
import * as Table from 'components/table';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Import } from 'types/domain';

const columnHelper = createColumnHelper<Import>();

const Imports = () => {
    const { accountId } = useCurrentAccount();
    const { pagination, setPagination, expanded, setExpanded } = useReactTableState();

    const { data: imports } = useSuspenseQuery({
        queryKey: accountQueryKeys.importsWithFilters(accountId, {
            page: pagination.pageIndex + 1,
        }),
        queryFn: () => fetchImports(accountId, { page: pagination.pageIndex + 1 }),
        refetchInterval: data =>
            data.state.data?.data.some(imp =>
                ['pending', 'processing', 'queued'].includes(imp.status),
            )
                ? 5000
                : false,
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('resource', {
                header: 'Import',
                cell: info => (
                    <div className={'flex flex-col justify-center'}>
                        <p className={'font-medium'}>{importFormatted(info.getValue())}</p>
                        {info.row.original.object_type !== 'account' && (
                            <p className={'text-gray-600'}>
                                {info.row.original.object_display_name}
                            </p>
                        )}
                    </div>
                ),
            }),

            columnHelper.accessor('rows', {
                header: 'Total Rows',
                cell: info => (
                    <div className={'flex items-center gap-x-2'}>
                        <p>{maskWholeNumber(info.getValue())}</p>
                    </div>
                ),
            }),

            columnHelper.accessor('errors', {
                header: 'Failed Rows',
                cell: info => <FailedRows report={info.row.original} accountId={accountId} />,
            }),

            columnHelper.accessor('created_at', {
                id: 'created_at',
                header: () => (
                    <div className={'flex items-baseline gap-1'}>
                        <p>Date</p>
                        <p className={'text-xs leading-5 text-gray-400'}>
                            ({DateTime.now().offsetNameShort})
                        </p>
                    </div>
                ),
                cell: info => (
                    <div>
                        {dateFromServer(info.getValue()).toLocaleString(DateTime.DATETIME_MED)}
                    </div>
                ),
            }),

            columnHelper.accessor('status', {
                header: 'Status',
                cell: info => (
                    <div className={'flex items-center gap-x-2'}>
                        <Badge status={info.getValue()} />
                    </div>
                ),
            }),

            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => <RowActions report={info.row.original} accountId={accountId} />,
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [accountId],
    );

    const table = useReactTable({
        data: imports?.data ?? [],
        columns,
        pageCount: imports?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            expanded,
        },
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            to: cell => `/accounts/${accountId}/settings/imports/${cell.row.original.id}`,
        },
    });

    return (
        <div>
            <Table.Root table={table} variant="bordered">
                <Box className={'flex flex-col overflow-hidden'}>
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'secondary'}>
                        <Table.EmptyIcon />
                        <Table.EmptyTitle>You haven't imported any files.</Table.EmptyTitle>
                    </Table.Empty>
                    <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                        <Table.PaginationPageNavigation />
                        <Table.PaginationPerPageDropdown />
                    </Table.Pagination>
                </Box>
            </Table.Root>
        </div>
    );
};

const FailedRows = ({ report, accountId }: { report: Import; accountId: number }) => {
    const { apiUrl } = useAppState();
    return (
        <div className={'flex items-center gap-x-2'}>
            {report.status === 'failed' && <EmptyData />}

            {report.status === 'succeeded' &&
                maskWholeNumber(report.error_count || errorCount(report))}
            {report.status === 'succeeded' && report.error_file && (
                <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                        <Button
                            variant="transparent"
                            color="error"
                            size="sm"
                            shape="square"
                            className={'ml-1 rounded-full bg-red-100'}
                            aria-label="Download failed records"
                            onClick={() => {
                                window.open(
                                    `${apiUrl}/dashboard/accounts/${accountId}/imports/${report.id}/download?failed=true`,
                                    '_blank',
                                );
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowsRotate} />
                        </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content>
                            Download failed records
                            <Tooltip.Arrow />
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>
            )}

            {['pending', 'processing', 'queued'].includes(report.status) && 0}
        </div>
    );
};

const RowActions = ({ report, accountId }: { report: Import; accountId: number }) => {
    const { apiUrl } = useAppState();

    return (
        <DropdownMenu.Root>
            <DropdownMenu.EllipsisTrigger className="float-right px-2 py-1" />
            <DropdownMenu.Content sideOffset={4} align={'end'}>
                <DropdownMenu.Item
                    onClick={() =>
                        window.open(
                            `${apiUrl}/dashboard/accounts/${accountId}/imports/${report.id}/download`,
                            '_blank',
                        )
                    }
                >
                    <div className={'flex items-center gap-x-2'}>
                        <FontAwesomeIcon
                            className={'w-4 text-gray-500'}
                            icon={faArrowDownToBracket}
                        />
                        Download File
                    </div>
                </DropdownMenu.Item>

                {!!report.error_file && ['succeed', 'failed'].includes(report.status) && (
                    <DropdownMenu.Item
                        onClick={() =>
                            window.open(
                                `${apiUrl}/dashboard/accounts/${accountId}/imports/${report.id}/download?failed=true`,
                                '_blank',
                            )
                        }
                    >
                        <div className={'flex items-center gap-x-2'}>
                            <FontAwesomeIcon className={'w-4 text-gray-500'} icon={faXmark} />
                            Download Failed Records
                        </div>
                    </DropdownMenu.Item>
                )}

                {report.status === 'succeeded' && isDeletableImport(report) && (
                    <ReactQueryErrorBoundary>
                        <Suspense fallback={<Loader />}>
                            <DeleteImportAlertDialog.Root importRecord={report}>
                                <DeleteImportAlertDialog.Trigger asChild>
                                    <DropdownMenu.Item
                                        className={'flex items-center gap-2 text-red-500'}
                                        onSelect={e => e.preventDefault()}
                                    >
                                        <FontAwesomeIcon className={'w-4'} icon={faTrash} />
                                        Delete imported{' '}
                                        {importFormatted(report.resource).toLowerCase()}
                                    </DropdownMenu.Item>
                                </DeleteImportAlertDialog.Trigger>
                            </DeleteImportAlertDialog.Root>
                        </Suspense>
                    </ReactQueryErrorBoundary>
                )}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};

export { Imports };
